/**
 * Autor: CI Sistemas de Informática Ltda. — AcessoiD
 * Onda 136 (08/10/2026): o perfil ALPR da câmera, lido e gravado pela plataforma.
 *
 * O modal lê a configuração de imagem da própria câmera (Intelbras, pela API dela), mostra
 * lado a lado o que está e o que o Compêndio de Câmeras 1.1 manda, e deixa o administrador
 * marcar o que gravar. Nada é gravado sem o clique e a confirmação; o que estava antes fica
 * guardado no servidor, e "Desfazer" grava de volta. A leitura (reler) nunca altera a câmera.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiRefreshCw, FiRotateCcw, FiSave, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const ESTADO = {
  igual: { classe: 'pc-igual', texto: 'igual' },
  equivalente: { classe: 'pc-equivalente', texto: 'equivalente' }, // 136b: firmware antigo, 0 = abaixo de 1 ms
  difere: { classe: 'pc-difere', texto: 'difere' },
  nao_lido: { classe: 'pc-nao-lido', texto: 'não lido' },
  ausente: { classe: 'pc-nao-lido', texto: 'não existe nesta câmera' },
};

export function textoValor(valor, legenda) {
  if (valor === null || valor === undefined || valor === '') return '—';
  return legenda ? `${legenda} (${valor})` : String(valor);
}

export function quandoLido(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const ParametrosCamera = ({ camera, onClose }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [gravando, setGravando] = useState(false);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [ultimo, setUltimo] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await api.get(`/api/cameras/${camera.id}/parametros`);
      setDados(r.data);
      setSelecionadas(new Set(r.data.itens.filter(i => i.aplicavel).map(i => i.chave)));
    } catch (e) {
      const d = e.response && e.response.data;
      setErro({ titulo: (d && d.error) || 'Não foi possível ler a câmera', detalhe: d && d.detalhe });
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [camera.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const grupos = useMemo(() => {
    const ordem = [];
    const por = {};
    for (const i of (dados ? dados.itens : [])) {
      if (!por[i.grupo]) { por[i.grupo] = []; ordem.push(i.grupo); }
      por[i.grupo].push(i);
    }
    return ordem.map(g => ({ grupo: g, itens: por[g] }));
  }, [dados]);

  const alternar = (chave) => {
    setSelecionadas(prev => { const n = new Set(prev); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });
  };

  const aplicar = async () => {
    const chaves = Array.from(selecionadas);
    if (!chaves.length) return;
    if (!window.confirm(`Gravar ${chaves.length} parâmetro(s) na câmera "${camera.name}"? O que está hoje fica guardado para desfazer.`)) return;
    setGravando(true);
    try {
      const r = await api.post(`/api/cameras/${camera.id}/parametros/aplicar`, { chaves });
      setUltimo({ acao: 'aplicar', ...r.data });
      if (r.data.resultado === 'ok') toast.success(`✅ ${r.data.confirmadas.length} parâmetro(s) gravado(s) e confirmado(s) na câmera`);
      else if (r.data.resultado === 'nada a gravar') toast(`Nada a gravar: a câmera já está no perfil`);
      else toast.error(`Gravação parcial: ${r.data.resultado}`);
      await carregar();
    } catch (e) {
      const d = e.response && e.response.data;
      toast.error((d && d.error) || 'Erro ao gravar na câmera');
    } finally {
      setGravando(false);
    }
  };

  const desfazer = async () => {
    const ult = dados && dados.historico && dados.historico.find(h => h.acao === 'aplicar');
    if (!ult) return;
    if (!window.confirm(`Desfazer a gravação de ${quandoLido(ult.applied_at)} (${ult.chaves.length} parâmetro(s)) na câmera "${camera.name}"?`)) return;
    setGravando(true);
    try {
      const r = await api.post(`/api/cameras/${camera.id}/parametros/desfazer`, { mudanca_id: ult.id });
      setUltimo({ acao: 'desfazer', ...r.data });
      if (r.data.resultado === 'ok') toast.success(`↩ ${r.data.confirmadas.length} parâmetro(s) de volta ao que era`);
      else toast.error(`Desfazer parcial: ${r.data.resultado}`);
      await carregar();
    } catch (e) {
      const d = e.response && e.response.data;
      toast.error((d && d.error) || 'Erro ao desfazer');
    } finally {
      setGravando(false);
    }
  };

  const podeDesfazer = !!(dados && dados.historico && dados.historico.length && dados.historico[0].acao === 'aplicar');

  return (
    <div className="roi-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="roi-modal pc-modal" role="dialog" aria-label={`Parâmetros de imagem da câmera ${camera.name}`}>
        <div className="roi-modal-header">
          <div>
            <h2>Parâmetros de imagem — {camera.name}</h2>
            <p className="roi-modal-subtitle">
              {dados
                ? <>{dados.modelo || 'modelo não informado'} em {dados.host}{dados.firmware ? ` · firmware ${dados.firmware}` : ''}{dados.firmware_so_inteiros ? ' (grava o obturador só em ms inteiros)' : ''} · perfil ALPR {dados.versao_do_perfil} · lido às {quandoLido(dados.lido_em)}</>
                : 'Lidos da própria câmera. Nada é gravado sem o seu clique.'}
            </p>
          </div>
          <button type="button" className="roi-close-btn" onClick={onClose} aria-label="Fechar"><FiX /></button>
        </div>

        {carregando && <div className="pc-aviso">Lendo a câmera…</div>}

        {!carregando && erro && (
          <div className="pc-erro">
            <strong>{erro.titulo}</strong>
            {erro.detalhe && <div className="pc-erro-detalhe">{erro.detalhe}</div>}
            <div className="pc-erro-dica">Só câmeras Intelbras ou Dahua alcançáveis pelo servidor respondem. A credencial usada é a da URL RTSP.</div>
          </div>
        )}

        {!carregando && dados && (
          <>
            <div className="pc-resumo">
              {dados.diferentes === 0
                ? <span className="pc-igual">A câmera está no perfil ALPR: nenhuma diferença.</span>
                : <span className="pc-difere">{dados.diferentes} parâmetro(s) diferem do perfil ALPR. Marque o que gravar.</span>}
              {dados.equivalentes > 0 && <span className="pc-equivalente"> · {dados.equivalentes} equivalente(s) pelo firmware antigo</span>}
              {dados.nao_lidos > 0 && <span className="pc-nao-lido"> · {dados.nao_lidos} não lido(s)</span>}
            </div>

            <div className="pc-tabela-wrap">
              <table className="pc-tabela">
                <thead>
                  <tr><th></th><th>Parâmetro</th><th>Na câmera</th><th>Perfil ALPR</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {grupos.map(g => (
                    <React.Fragment key={g.grupo}>
                      <tr className="pc-grupo"><td colSpan={5}>{g.grupo}</td></tr>
                      {g.itens.map(i => {
                        const est = ESTADO[i.estado] || ESTADO.nao_lido;
                        return (
                          <tr key={i.chave} className={i.estado === 'difere' ? 'pc-linha-difere' : ''}>
                            <td>
                              {i.aplicavel && (
                                <input type="checkbox" checked={selecionadas.has(i.chave)} onChange={() => alternar(i.chave)} aria-label={`Gravar ${i.rotulo}`} />
                              )}
                            </td>
                            <td title={i.chave}>{i.rotulo}</td>
                            <td className="pc-valor">{textoValor(i.atual, i.atual_texto)}{i.nota && <div className="pc-nota">{i.nota}</div>}</td>
                            <td className="pc-valor">{textoValor(i.ideal, i.ideal_texto)}</td>
                            <td><span className={`pc-estado ${est.classe}`}>{est.texto}</span></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {dados.informativos && dados.informativos.length > 0 && (
              <div className="pc-informativos">
                <strong>Stream principal (só leitura):</strong>{' '}
                {dados.informativos.map(i => <span key={i.chave} className="pc-info-item">{i.rotulo}: {textoValor(i.valor)}</span>)}
                {dados.informativos.filter(i => i.alerta).map(i => <div key={`a-${i.chave}`} className="pc-alerta">⚠ {i.rotulo}: {i.alerta}</div>)}
              </div>
            )}

            {ultimo && (
              <div className="pc-ultimo">
                {ultimo.acao === 'aplicar'
                  ? <>Última gravação: {ultimo.pedidas.length} pedida(s), {ultimo.confirmadas.length} confirmada(s) pela releitura{ultimo.nao_confirmadas && ultimo.nao_confirmadas.length ? ` — não confirmadas: ${ultimo.nao_confirmadas.join(', ')}` : ''}.</>
                  : <>Desfeito: {ultimo.confirmadas.length} de {ultimo.restauradas.length} parâmetro(s) de volta ao valor anterior.</>}
              </div>
            )}

            {dados.historico && dados.historico.length > 0 && (
              <div className="pc-historico">
                <strong>Gravações por aqui:</strong>
                <ul>
                  {dados.historico.slice(0, 5).map(h => (
                    <li key={h.id}>{quandoLido(h.applied_at)} · {h.acao === 'desfazer' ? 'desfazer' : 'aplicar'} · {h.chaves.length} parâmetro(s) · {h.applied_by || '—'} · {h.resultado || ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="roi-modal-actions pc-acoes">
          <button type="button" className="btn btn-info btn-sm" onClick={carregar} disabled={carregando || gravando}><FiRefreshCw /> Reler</button>
          <button type="button" className="btn btn-warning btn-sm" onClick={desfazer} disabled={!podeDesfazer || gravando || carregando} title={podeDesfazer ? 'Grava de volta os valores anteriores à última gravação feita por aqui' : 'Não há gravação feita por aqui para desfazer'}><FiRotateCcw /> Desfazer última gravação</button>
          <button type="button" className="btn btn-success btn-sm" onClick={aplicar} disabled={!dados || gravando || carregando || selecionadas.size === 0}><FiSave /> {gravando ? 'Gravando…' : `Aplicar perfil ALPR (${selecionadas.size})`}</button>
          <button type="button" className="btn btn-sm" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default ParametrosCamera;
